
import {
  defineComponent, onBeforeMount, onMounted, reactive, watch,
} from 'vue';
import {
  IDTORastreabilidadeLote,
  IDTORastreabilidadeOrigemLote,
  IDTORastreabilidadeUtilizacaoLote,
  IDTORastreabilidadeLotesGerados,
  IParametroConsultaRastreabilidadeLote,
} from '@/models/DTO/Estoques/Rastreabilidade/RastreabilidadeLote/IDTORastreabilidadeLote';
import { useGradeBase } from '@/core/composables/GradeBase';
import { useTelaBase } from '@/core/composables/TelaBase';
import TituloPadrao from '@/core/components/Tela/TituloPadrao.vue';
import Card from '@/core/components/Tela/Card.vue';
import BuscaGeral from '@/core/components/BuscaAvancada/BuscaGeral.vue';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import ServicoRastreabilidade from '@/servicos/Estoques/ServicoRastreabilidade';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import storeSistema from '@/store/storeSistema';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';

export default defineComponent({
  name: 'RastreabilidadeLoteIndex',
  components: {
    TituloPadrao,
    Card,
    BuscaGeral,
    MensagemSemDados,
  },
  setup() {
    const {
      telaBase, verificaConceitoParaApresentarEmpresas, preencherEmpresasDisponiveis, apresentarMensagemErro,
    } = useTelaBase();
    const { gradeBase } = useGradeBase();
    const { gradeBase: gradeUtilizacoes } = useGradeBase();
    const { gradeBase: gradeGerados } = useGradeBase();
    const servicoRastreabilidade = new ServicoRastreabilidade();
    servicoRastreabilidade.requisicaoSistema();
    const state = reactive({
      // Filtros
      buscaRapida: '',
      empresa: 0,

      // Grade Principal
      lotes: [] as IDTORastreabilidadeLote[],
      loteSelecionado: null as IDTORastreabilidadeLote | null,
      carregandoGrade: false,

      // Origem
      origem: null as IDTORastreabilidadeOrigemLote | null,
      origensLoading: false,

      // Utilizações
      utilizacoes: [] as IDTORastreabilidadeUtilizacaoLote[],
      utilizacoesLoading: false,
      utilizacaoSelecionada: null as IDTORastreabilidadeUtilizacaoLote | null,
      mostrarGerados: false,

      // Lotes Gerados
      gerados: [] as IDTORastreabilidadeLotesGerados[],
      geradosLoading: false,

      // Configuração da Grade
      windowHeight: 400,
    });

    /**
     * Lógica para redimensionamento da grade principal
     */
    function initResize(mouseDownEvent: MouseEvent) {
      // Previne o início do redimensionamento se não for com o botão esquerdo do mouse
      if (mouseDownEvent.button !== 0) return;

      const startY = mouseDownEvent.clientY;
      const startHeight = state.windowHeight;

      const doResize = (mouseMoveEvent: MouseEvent) => {
        const deltaY = mouseMoveEvent.clientY - startY;
        const newHeight = startHeight + deltaY;

        // Define uma altura mínima de 100px e máxima para não sobrepor totalmente os outros componentes
        const maxHeight = window.innerHeight - 400; // Reserva 400px para os outros componentes
        state.windowHeight = Math.max(100, Math.min(newHeight, maxHeight));
      };

      const stopResize = () => {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', doResize);
        window.removeEventListener('mouseup', stopResize);
      };

      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', doResize);
      window.addEventListener('mouseup', stopResize);
    }

    /**
     * Configura as colunas da grade principal
     */
    function configurarColunas() {
      gradeBase.colunas = [
        {
          title: 'Cód. Def.',
          dataIndex: 'codigoDefinicao',
          key: 'codigoDefinicao',
          width: 0,
          position: -1,
          visible: false,
        },
        {
          title: 'Código',
          dataIndex: 'codigoInterno',
          key: 'codigoInterno',
          width: 100,
          align: 'left',
          position: 0,
          visible: true,
        },
        {
          title: 'Produto',
          dataIndex: 'produtoDescricao',
          key: 'produtoDescricao',
          width: 200,
          align: 'left',
          position: 0,
          visible: true,
        },
        {
          title: 'Lote',
          dataIndex: 'lote',
          key: 'lote',
          width: 120,
          align: 'center',
          position: 0,
          visible: true,
        },
        {
          title: 'Data Criação',
          dataIndex: 'dataCriacao',
          key: 'dataCriacao',
          width: 110,
          align: 'center',
          position: 0,
          visible: true,
        },
        {
          title: 'Validade',
          dataIndex: 'dataValidade',
          key: 'dataValidade',
          width: 110,
          align: 'center',
          position: 0,
          visible: true,
        },
        {
          title: 'Qtd. Inicial',
          dataIndex: 'qtdInicial',
          key: 'qtdInicial',
          width: 100,
          align: 'right',
          position: 0,
          visible: true,
        },
        {
          title: 'Saldo Atual',
          dataIndex: 'saldoAtual',
          key: 'saldoAtual',
          width: 100,
          align: 'right',
          position: 0,
          visible: true,
        },
        {
          title: 'Custo Total',
          dataIndex: 'custoTotalEntrada',
          key: 'custoTotalEntrada',
          width: 120,
          align: 'right',
          position: 0,
          visible: true,
        },
      ];

      gradeUtilizacoes.colunas = [
        {
          title: 'Código',
          dataIndex: 'codigo',
          key: 'codigo',
          width: 0,
          position: -1,
          visible: false,
        },
        {
          title: 'Tipo',
          dataIndex: 'recurso',
          key: 'recurso',
          width: 100,
          align: 'left',
          position: 0,
          visible: true,
        },
        {
          title: 'Documento',
          dataIndex: 'documento',
          key: 'documento',
          width: 120,
          align: 'center',
          position: 0,
          visible: true,
        },
        {
          title: 'Data',
          dataIndex: 'data',
          key: 'data',
          width: 110,
          align: 'center',
          position: 0,
          visible: true,
        },
        {
          title: 'Cliente',
          dataIndex: 'nomeDestino',
          key: 'nomeDestino',
          width: 180,
          align: 'left',
          position: 0,
          visible: true,
        },
        {
          title: 'Qtd. Utilizada',
          dataIndex: 'quantidade',
          key: 'quantidade',
          width: 120,
          align: 'right',
          position: 0,
          visible: true,
        },
      ];

      gradeGerados.colunas = [
        {
          title: 'Lote Gerado',
          dataIndex: 'lote',
          key: 'lote',
          width: 120,
          align: 'center',
          position: 0,
          visible: true,
        },
        {
          title: 'Produto',
          dataIndex: 'produto',
          key: 'produto',
          width: 200,
          align: 'left',
          position: 0,
          visible: true,
        },
        {
          title: 'Data Produção',
          dataIndex: 'dataFabricacao',
          key: 'dataFabricacao',
          width: 110,
          align: 'center',
          position: 0,
          visible: true,
        },
        {
          title: 'Quantidade',
          dataIndex: 'quantidade',
          key: 'quantidade',
          width: 120,
          align: 'right',
          position: 0,
          visible: true,
        },
      ];
    }

    /**
     * Busca lotes com base nos filtros
     */
    async function buscarLotes() {
      state.carregandoGrade = true;
      state.loteSelecionado = null;
      state.lotes = [];

      try {
        const filtros: IParametroConsultaRastreabilidadeLote = {
          empresa: state.empresa,
          lote: state.buscaRapida.trim() || undefined,
          buscaDinamica: state.buscaRapida.trim() || undefined,
        };

        const resultado = await servicoRastreabilidade.postObterRelatorio(filtros);
        state.lotes = Array.isArray(resultado) ? resultado : (resultado.dados || []);
        console.log('Lotes buscados:', state.lotes);
      } catch (erro) {
        console.error('Erro ao buscar lotes:', erro);
      } finally {
        state.carregandoGrade = false;
      }
    }

    /**
     * Carrega os detalhes de um lote selecionado
     * Faz requisições simultâneas para origem, utilizações e lotes gerados
     */
    async function carregarDetalhesLote() {
      if (!state.loteSelecionado) {
        return;
      }

      state.origensLoading = true;
      state.utilizacoesLoading = true;
      state.geradosLoading = false; // Gerados são carregados sob demanda

      // Limpa os dados anteriores
      state.origem = null;
      state.utilizacoes = [];
      state.gerados = [];
      state.utilizacaoSelecionada = null;
      state.mostrarGerados = false;

      try {
        const codigoDefinicao = state.loteSelecionado.codigoDefinicao;
        const lote = state.loteSelecionado.lote;

        // Requisições simultâneas via Promise.all
        const [origemData, utilizacoesData] = await Promise.all([
          servicoRastreabilidade.getOrigem(codigoDefinicao, lote),
          servicoRastreabilidade.getUtilizacoes(codigoDefinicao, lote),
        ]);
        console.log('Detalhes do lote carregados:', { origemData, utilizacoesData });

        state.origem = Array.isArray(origemData) ? origemData[0] : origemData;
        // Verifica se o objeto de origem está vazio e o define como nulo para exibir a mensagem correta
        if (state.origem && Object.keys(state.origem).length === 0) {
          state.origem = null;
        }

        state.utilizacoes = utilizacoesData;
      } catch (erro) {
        console.error('Erro ao carregar detalhes do lote:', erro);
        apresentarMensagemErro('Não foi possível carregar os detalhes do lote selecionado.');
      } finally {
        state.origensLoading = false;
        state.utilizacoesLoading = false;
      }
    }

    /**
     * Carrega os lotes gerados a partir de uma utilização selecionada
     */
    async function carregarLotesGerados() {
      if (!state.loteSelecionado || !state.utilizacaoSelecionada) {
        return;
      }

      state.geradosLoading = true;
      state.gerados = [];
      try {
        const { codigoDefinicao, lote } = state.loteSelecionado;
        const codigoOP = state.utilizacaoSelecionada.codigo;
        const geradosData = await servicoRastreabilidade.getGerados(codigoOP, codigoDefinicao, lote);
        state.gerados = geradosData;
      } catch (erro) {
        console.error('Erro ao carregar lotes gerados:', erro);
        apresentarMensagemErro('Não foi possível carregar os lotes gerados.');
      } finally {
        state.geradosLoading = false;
      }
    }

    /**
     * Define a classe da linha para a grade de lotes rastreados
     */
    function obterClasseLinhaLote(record: IDTORastreabilidadeLote) {
      // Se nenhum lote estiver selecionado ou se o ID do lote selecionado for undefined,
      return state.loteSelecionado?.id === record.id ? 'ss-linha-selecionada' : '';
    }

    /**
     * Define a classe da linha para a grade de utilizações
     */
    function obterClasseLinhaUtilizacao(record: IDTORastreabilidadeUtilizacaoLote) {
      return state.utilizacaoSelecionada?.codigo === record.codigo ? 'ss-linha-selecionada' : '';
    }

    /**
     * Define customRow para permitir click e seleção
     */
    function customRow(record: IDTORastreabilidadeLote) {
      return {
        onClick: () => {
          state.loteSelecionado = record;
          carregarDetalhesLote();
        },
      };
    }

    /**
     * Define customRow para a grade de utilizações
     */
    function customRowUtilizacao(record: IDTORastreabilidadeUtilizacaoLote) {
      return {
        onClick: () => {
          state.utilizacaoSelecionada = record;
          state.mostrarGerados = true;

          // Validação para carregar lotes gerados apenas para produção (PCP)
          if (record.recurso?.includes('PCP') && record.codigo > 0) {
            carregarLotesGerados();
          } else {
            // Se não for produção, limpa os dados de lotes gerados
            state.gerados = [];
            state.geradosLoading = false;
          }
        },
      };
    }

    /**
     * Ajusta altura da janela para scroll da grade principal
     */
    function ajustarAlturaJanela() {
      // Subtrai um valor aproximado da altura dos outros componentes (título, filtros, etc)
      // para definir uma altura para a grade principal.
      const alturaCalculada = window.innerHeight - 480;
      // Garante uma altura mínima para a grade, evitando que ela desapareça em telas menores.
      state.windowHeight = Math.max(200, alturaCalculada);
    }

    onBeforeMount(async () => {
      telaBase.empresasSelecionadas = storeSistema.getters.empresasUsuarioAutenticado();
      verificaConceitoParaApresentarEmpresas();
      await preencherEmpresasDisponiveis();
      state.empresa = storeSistema.getters.codigoEmpresaOperacao();
    });

    onMounted(() => {
      configurarColunas();
      ajustarAlturaJanela();
      window.addEventListener('resize', ajustarAlturaJanela);
    });

    watch(
      () => state.empresa,
      () => {
        state.lotes = [];
        state.loteSelecionado = null;
      },
    );

    return {
      state,
      telaBase,
      gradeBase,
      gradeUtilizacoes,
      gradeGerados,
      buscarLotes,
      carregarDetalhesLote,
      carregarLotesGerados,
      apresentarMensagemErro,
      customRow,
      customRowUtilizacao,
      obterClasseLinhaLote,
      obterClasseLinhaUtilizacao,
      initResize,
      UtilitarioMascara,
      UtilitarioData,
    };
  },
});
